import queryString from "query-string";
import React, { useEffect, useRef, useState } from "react";
import { LuEye, LuEyeOff, LuStar } from "react-icons/lu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, StatefulButton } from "../ui/button";
import { getPaddleBillingClient } from "../../utils/paddleBilling";
import chromeWebStoreBadge from "../../assets/login/chrome-store-badge.png";
import "./login.css";
import { CircleCheck } from "lucide-react";

const loginLogo = "https://app.groupboss.io/static/media/logo.d96bc6c1f4b02f704ac1.png"

function LoginField({
  id,
  label,
  name,
  onChange,
  type = "text",
  value,
  error,
  disabled = false,
  children,
}) {
  return (
    <div className={`login-field${error ? " login-field--error" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <div className="login-input-wrap">
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? id + "-error" : undefined}
          className={"login-input" + (children ? " login-input--password" : "")}
        />
        {children}
      </div>
      {error && (
        <span className="login-field__error" id={id + "-error"} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

const testimonials = [
  {
    id: "mark-thorsen",
    author: "Mark Thorsen",
    quote:
      "It is totally worth the money - we only wish we found it sooner.",
    emphasis: "wish we found it sooner.",
  },
  {
    id: "jeannie-pol",
    author: "Jeannie Pol",
    quote:
      "I am loving Groupboss. It's made my life so much easier! Saves me a great deal of time each day!",
    emphasis: "made my life so much easier!",
  },
  {
    id: "jerry-ilao",
    author: "Jerry Ilao",
    quote:
      "10 out of 10 for the product and customer services.",
    emphasis: "10 out of 10",
  },
  {
    id: "nele-v",
    author: "Nele V",
    quote:
      "Great tool for Facebook Group owners.",
    emphasis: "Facebook Group owners.",
  },
  {
    id: "tim-hewitt",
    author: "Tim Hewitt",
    quote:
      "Without Groupboss, this was taking us 30-45 minutes or more every day.",
  },
];

function HighlightedQuote({ quote, emphasis }) {
  const [before, after] = quote.split(emphasis);

  return (
    <>
      {before}
      <mark className="login-testimonial-highlight">{emphasis}</mark>
      {after}
    </>
  );
}

function TestimonialCard() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const activeTestimonial = testimonials[activeIndex];

  useEffect(() => {
    if (isPaused) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % testimonials.length);
    }, 6000);

    return () => window.clearInterval(intervalId);
  }, [isPaused]);

  return (
    <aside
      className="login-testimonial"
      aria-label="Customer testimonials"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsPaused(false);
        }
      }}>
      <div className="login-testimonial-card">
        <div className="login-testimonial-slider" aria-live="polite">
          <div className="login-testimonial-slide" key={activeTestimonial.id}>
            <blockquote className="login-testimonial-copy">
              <span className="login-quote-mark login-quote-mark--open" aria-hidden="true">
                “
              </span>
              <HighlightedQuote
                quote={activeTestimonial.quote}
                emphasis={activeTestimonial.emphasis}
              />
              <span className="login-quote-mark login-quote-mark--close" aria-hidden="true">
                ”
              </span>
            </blockquote>

            <div className="login-testimonial-author">
              <span className="login-testimonial-author-name">
                {activeTestimonial.author}
              </span>
              <span className="login-testimonial-verified" aria-label="Verified customer">
                <CircleCheck aria-hidden="true" size={16} strokeWidth={2.5} />
              </span>
            </div>
          </div>
        </div>

        <div className="login-testimonial-controls" aria-label="Choose a testimonial">
          {testimonials.map((testimonial, index) => (
            <button
              className={
                "login-testimonial-dot" +
                (index === activeIndex ? " login-testimonial-dot--active" : "")
              }
              key={testimonial.id}
              type="button"
              aria-label={`Show testimonial from ${testimonial.author}`}
              aria-current={index === activeIndex ? "true" : undefined}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>

        <div className="login-testimonial-proof">
        <a
          className="login-store-link"
          href="https://chrome.google.com/webstore/detail/groupboss/gakcpcoikgklfbajjkdaomcfkpeiobfl?hl=en-US"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open Groupboss in the Chrome Web Store"
        >
          <img loading="lazy" src={chromeWebStoreBadge} alt="Available in the Chrome Web Store" />
        </a>

        <div className="login-rating" aria-label="Rated 4.7 out of 5 on the Chrome Web Store">
          <span className="login-rating-stars" aria-hidden="true">
            {[0, 1, 2, 3].map((star) => (
              <LuStar className="login-rating-star" key={star} fill="currentColor" size={18} strokeWidth={1.8} />
            ))}
            <span className="login-rating-star-shell">
              <LuStar className="login-rating-star login-rating-star--empty" size={18} strokeWidth={1.8} />
              <span className="login-rating-star-fill">
                <LuStar className="login-rating-star" fill="currentColor" size={18} strokeWidth={1.8} />
              </span>
            </span>
          </span>
          <span className="login-rating-value">4.7 <span>/ 5</span></span>
        </div>
        </div>
      </div>
    </aside>
  );
}

function persistAuthSession(authData) {
  localStorage.setItem("access_token", authData.auth.access_token);
  localStorage.setItem("refresh_token", authData.auth.refresh_token);
  localStorage.setItem("show_board", authData.show_board);

  const emails = [];

  if (authData.user) {
    localStorage.setItem("user_id", authData.user.id);

    authData.user.emails.forEach((email) => {
      if (email.default) {
        localStorage.setItem("user_default_email", email.email);
      }

      emails.push(email);
    });

    localStorage.setItem("user_emails", JSON.stringify(emails));
    localStorage.setItem("user_first_name", authData.user.first_name);
    localStorage.setItem("user_last_name", authData.user.last_name);
  }
}

function Login(props) {
  const navigate = useNavigate();
  const location = useLocation();

  const prevAuthRef = useRef();
  const handledLoginRef = useRef(false);
  const { auth, login, loginRedirectReset } = props;

  const [loginData, setLoginData] = useState({
    emailAddress: "",
    password: "",
    isEmailPresent: false,
    showPassword: false,
    loading: false,
    saveErrors: {},
  });

  useEffect(() => {
    prevAuthRef.current = auth;
  }, [auth]);

  const prevAuth = prevAuthRef.current;

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token !== null) {
      navigate("/user/dashboard");
    }
    if (location.search) {
      const queryParsed = queryString.parse(location.search);
      if (!(queryParsed.email && queryParsed.email.length > 0)) {
        navigate("/login");
      } else {
        setLoginData((prevState) => ({
          ...prevState,
          emailAddress: queryParsed.email,
          isEmailPresent: true,
        }));
      }
    }
  }, [location.search, navigate]);

  useEffect(() => {
    let cancelled = false;

    getPaddleBillingClient().catch(() => {
      if (!cancelled) {
        // Paddle verification happens from this public page; failures here
        // should not block login if the script is temporarily unavailable.
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const saveErrors = {};
    const authData = auth?.auth;
    const accessToken = authData?.auth?.access_token;

    if (auth?.errorMessage && auth?.errorMessage !== prevAuth?.auth?.errorMessage) {
      if (
        auth?.errorMessage ===
        "no active account found with the given credentials"
      ) {
        saveErrors.emailAddress =
          "There is no account with this email. Please Sign Up first";
      } else if (auth?.errorMessage === "provided password does not match") {
        saveErrors.password = "Password is incorrect";
      }
      setLoginData((prevState) => ({
        ...prevState,
        saveErrors,
      }));
    }

    if (!auth.redirect || !accessToken || handledLoginRef.current) {
      return;
    }

    handledLoginRef.current = true;
    persistAuthSession(authData);

    if (authData.show_board) {
      navigate("/user/getting-started", { replace: true });
    } else {
      navigate("/user/dashboard", { replace: true });
    }

    loginRedirectReset();
  }, [auth, loginRedirectReset, navigate, prevAuth]);

  useEffect(() => {
    if (!auth?.isLoading && !auth?.redirect) {
      handledLoginRef.current = false;
    }
  }, [auth?.isLoading, auth?.redirect]);

  const onChangeHandle = (event) => {
    const { name, value } = event.target;
    setLoginData((prevState) => ({
      ...prevState,
      saveErrors: {},
      [name]: value,
    }));
  };

  const checkError = () => {
    const saveErrors = {};
    let formIsValid = true;
    const email = loginData.emailAddress.trim();

    if (email === "") {
      formIsValid = false;
      saveErrors.emailAddress = "Email address can't be empty";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formIsValid = false;
      saveErrors.emailAddress = "Please insert a valid email address";
    }

    if (loginData.password === "") {
      formIsValid = false;
      saveErrors.password = "Password can't be empty";
    }

    setLoginData((prevState) => ({
      ...prevState,
      saveErrors,
    }));

    return formIsValid;
  };

  const onSubmitHandler = (event) => {
    event.preventDefault();
    if (checkError()) {
      login({
        email: loginData.emailAddress,
        password: loginData.password,
      });
    }
  };

  const toggleShow = () => {
    setLoginData((prevState) => ({
      ...prevState,
      showPassword: !prevState.showPassword,
    }));
  };

  return (
    <main className="login-page">
      <div className="login-shell">
        <section className="login-main" aria-label="Sign in">
          <a className="login-brand" href="https://groupboss.io" aria-label="Groupboss home">
            <img loading="lazy" src={loginLogo} alt="Groupboss" />
          </a>

          <form className="login-form" onSubmit={onSubmitHandler} noValidate>
            <div className="login-fields">
              <LoginField
                id="emailAddress"
                label="Email"
                name="emailAddress"
                type="email"
                value={loginData.emailAddress}
                onChange={onChangeHandle}
                error={loginData.saveErrors.emailAddress}
                disabled={loginData.isEmailPresent}
              />

              <LoginField
                id="password"
                label="Password"
                name="password"
                type={loginData.showPassword ? "text" : "password"}
                value={loginData.password}
                onChange={onChangeHandle}
                error={loginData.saveErrors.password}
              >
                <Button
                  className="login-password-toggle"
                  size="icon-sm"
                  type="button"
                  onClick={toggleShow}
                  whileHover={undefined}
                  whileTap={undefined}
                  variant="ghost"
                  aria-label={loginData.showPassword ? "Hide password" : "Show password"}
                  title={loginData.showPassword ? "Hide password" : "Show password"}
                >
                  {loginData.showPassword ? (
                    <LuEyeOff aria-hidden="true" size={18} />
                  ) : (
                    <LuEye aria-hidden="true" size={18} />
                  )}
                </Button>
              </LoginField>
            </div>

            <div className="login-actions">
              <StatefulButton
                className="login-button"
                type="submit"
                loadingText="Signing in…"
                state={auth?.isLoading ? "loading" : "idle"}
              >
                Sign In
              </StatefulButton>
              <Link className="login-secondary-link" to="/forgot-password">
                Forgot Password?
              </Link>
              <div className="login-signup">
                <span>Don't have an Account?</span>
                <a
                  className="login-signup-link"
                  href="https://groupboss.io/pricing"
                  target="_blank"
                  rel="noopener noreferrer">
                  Sign Up
                </a>
              </div>
            </div>

            <nav className="login-legal" aria-label="Legal">
              <a
                href="https://groupboss.io/terms-of-service"
                target="_blank"
                rel="noopener noreferrer">
                Terms of Services
              </a>
              <span className="login-legal-separator" aria-hidden="true" />
              <a
                href="https://groupboss.io/privacy-policy"
                target="_blank"
                rel="noopener noreferrer">
                Privacy Policy
              </a>
            </nav>
          </form>
        </section>

        <TestimonialCard />
      </div>
    </main>
  );
}

export default Login;
